import { useCreateFlowSession } from "@/api/flow_sessions";
import { useCanvasHarnessDraft } from "@/hooks/useHarnessDraft";
import { emitCanvasOpenFloatingSession } from "@/pages/canvas/context/CanvasFloatingChatContext";
import { FLOW_SESSION_MODES } from "@cohesive/api-client";
import { useCallback, useRef } from "react";

export function useCanvasRailNewChat(canvasId: string, canEdit: boolean) {
  const createFlowSession = useCreateFlowSession();
  // The rail starts the chat before the user has typed, so the canvas's standing choice is the
  // only one there is to stamp — the same one its composers offer.
  const [, , harnessCreateFields, harnessReady] =
    useCanvasHarnessDraft(canvasId);
  // Set before the create's first await: isPending only flips on the next render, so a second
  // click in the same frame would mint a second chat.
  const creating = useRef(false);

  const handleNewChat = useCallback(async () => {
    if (!canEdit || !canvasId || !harnessReady || creating.current) return;
    creating.current = true;
    try {
      const newSession = await createFlowSession.mutateAsync({
        mode: FLOW_SESSION_MODES.CANVAS_CHAT,
        class: "canvas",
        canvas_id: canvasId,
        ...harnessCreateFields,
      });
      emitCanvasOpenFloatingSession({
        canvasId,
        flowSessionId: newSession.id,
      });
    } finally {
      creating.current = false;
    }
  }, [canEdit, canvasId, createFlowSession, harnessCreateFields, harnessReady]);

  return {
    handleNewChat,
    // Also held until the catalog says which agent the chat starts on.
    isCreatingChat: createFlowSession.isPending || !harnessReady,
  };
}
